import React, { useEffect, useState } from 'react'
import { api } from '../../services/api'

/**
 * What GET /api/monetization/fees reports: the platform fee only. No Stripe
 * fee figure is shown until Stripe's fees are verified, so there's no
 * "you receive" total either: it would leave Stripe's share out.
 */
export interface FeeRates {
  platform_fee_rate: number
  payout_minimum_cents?: number
}

/** In place of a Stripe fee number (the backend sends the same text). */
export const STRIPE_FEES_NOTE = '+ Stripe processing fees, shown at payout'

const isRates = (data: any): data is FeeRates =>
  !!data && typeof data.platform_fee_rate === 'number'

/** The creator's platform fee, or null when it couldn't be read (then: "fees shown at payout"). */
export const useFeeRates = (): FeeRates | null => {
  const [rates, setRates] = useState<FeeRates | null>(null)
  useEffect(() => {
    let live = true
    const load = async () => {
      try {
        const { data } = await api.get('/api/monetization/fees')
        if (live) setRates(isRates(data) ? data : null)
      } catch {
        if (live) setRates(null)
      }
    }
    load()
    return () => {
      live = false
    }
  }, [])
  return rates
}

/** The platform fee on one payment, rounded down as payment_service records it. */
export const platformFeeCents = (priceCents: number, rates: FeeRates): number =>
  Math.floor(priceCents * rates.platform_fee_rate)

const pct = (rate: number) => `${+(rate * 100).toFixed(2)}%`
const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`

/** "FinCreator fee 10% + Stripe processing fees, shown at payout", or the fallback. */
export const feeTerms = (rates: FeeRates | null): string =>
  rates ? `FinCreator fee ${pct(rates.platform_fee_rate)} ${STRIPE_FEES_NOTE}` : 'Fees shown at payout'

interface FeeEstimateProps {
  priceCents: number | null
  per: string
  rates: FeeRates | null
}

/** "FinCreator fee: 10% ($1.50 per month) + Stripe processing fees, shown at payout." */
const FeeEstimate: React.FC<FeeEstimateProps> = ({ priceCents, per, rates }) => {
  if (!rates) {
    return <p className="text-xs text-gray-500">Fees shown at payout.</p>
  }
  if (priceCents == null || priceCents <= 0) return null
  return (
    <p className="text-xs text-gray-600" data-testid="fee-estimate">
      FinCreator fee: {pct(rates.platform_fee_rate)}{' '}
      <span className="font-semibold text-gray-900">
        ({dollars(platformFeeCents(priceCents, rates))} per {per})
      </span>{' '}
      {STRIPE_FEES_NOTE}.
    </p>
  )
}

export default FeeEstimate
